import { ReactNode, createContext, useContext, useReducer } from 'react'
import { IContext } from './IContext'
import { CartItem } from '../hooks/useCart'
import { AuthUser } from '../interfaces/AuthUser'


type Action =
  | { type: 'setCart', basketFurniture: CartItem[] }
  | { type: 'setIsAuth', isAuth: boolean }
  | { type: 'setIsAuthInvoice', isAuthInvoice: boolean }
  | { type: 'setAuthSession', user: AuthUser | null, authReady: boolean }
  | { type: 'setModalContent', modalContent: React.ReactNode }
  | { type: 'setShowModal', showModal: boolean }
  | { type: 'setLoading', loading: boolean }

type Dispatch = (action: Action) => void

type ContextProviderProps = { children: ReactNode }

const CountStateContext = createContext<
  { state: IContext; dispatch: Dispatch } | undefined
>(undefined)


const countReducer = (state: IContext, action: Action) => {
  switch (action.type) {

    case 'setShowModal': {
      return {
        ...state,
        showModal: action.showModal,
      }
    }

    case 'setModalContent': {
      return {
        ...state,
        modalContent: action.modalContent,
      }
    }

    case 'setCart': {
      return {
        ...state,
        basketFurniture: action.basketFurniture,
      }
    }

    case 'setIsAuth': {
      return {
        ...state,
        isAuth: action.isAuth,
      }
    }

    case 'setIsAuthInvoice': {
      return {
        ...state,
        isAuthInvoice: action.isAuthInvoice,
      }
    }

    case 'setAuthSession': {
      const permissions = action.user?.permissions || []
      const all = action.user?.role?.toLowerCase() === 'superadmin' || permissions.includes('*')
      return {
        ...state,
        currentUser: action.user,
        authReady: action.authReady,
        isAuth: all || permissions.includes('admin.access'),
        isAuthInvoice: all || permissions.includes('invoices.read'),
      }
    }

    case 'setLoading': {
      return {
        ...state,
        loading: action.loading,
      }
    }

    default: {
      throw new Error(`Unhandled action type.`)
    }
  }
}

const ContextProvider = ({ children }: ContextProviderProps) => {
  const [state, dispatch] = useReducer(countReducer, {
    basketFurniture: [],
    isAuth: false,
    isAuthInvoice: undefined,
    authReady: false,
    currentUser: null,
    modalContent: null,
    showModal: false,
    loading: false,
  })
  const value = { state, dispatch }
  return (
    <CountStateContext.Provider value={value}>
      {children}
    </CountStateContext.Provider>
  )
}

const useGlobalState = () => {
  const context = useContext(CountStateContext)
  if (context === undefined) {
    throw new Error('useGlobalState must be used within a ContextProvider')
  }
  return context
}

export { ContextProvider, useGlobalState }
